import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Circle, Ruler, Star, Award, Search, X } from 'lucide-react';
import Layout from '../components/Layout';
import { GolfCourse, apiService } from '../services/api';

interface CourseCardProps {
  course: GolfCourse;
}

const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* 图片区域 */}
      <Link to={`/courses/${course.id}`}>
        <div className="relative h-48 bg-gray-200 cursor-pointer group">
          {!imageError ? (
            <img
              src={course.image_url}
              alt={`${course.name} golf course`}
              className={`w-full h-full object-cover transition-all duration-300 ${
                imageLoaded ? 'opacity-100 group-hover:scale-105' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : null}

          {/* 图片加载占位符 */}
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          )}

          {/* 图片错误占位符 */}
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-green-100 to-blue-100">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 bg-green-500 rounded-full flex items-center justify-center">
                  <Circle className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-gray-600">{course.name}</p>
              </div>
            </div>
          )}

          {/* 城市标签 */}
          <div className="absolute top-3 left-3">
            <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
              <MapPin className="w-3 h-3 text-green-600" />
              <span className="text-xs font-medium text-gray-700">{course.city}</span>
            </div>
          </div>
        </div>
      </Link>

      {/* 内容区域 */}
      <div className="p-6">
        {/* 球场名称 */}
        <Link to={`/courses/${course.id}`}>
          <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors duration-200 cursor-pointer">{course.name}</h2>
        </Link>
        
        {/* 描述 */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {course.description}
        </p>
        
        {/* 统计信息 */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Circle className="w-4 h-4 text-green-600" />
            <div>
              <p className="text-xs text-gray-500">Holes</p>
              <p className="text-sm font-semibold text-gray-900">{course.holes}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Ruler className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-xs text-gray-500">Length</p>
              <p className="text-sm font-semibold text-gray-900">{course.length_yards?.toLocaleString()} yds</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-xs text-gray-500">Par</p>
              <p className="text-sm font-semibold text-gray-900">{course.par}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-orange-600" />
            <div>
              <p className="text-xs text-gray-500">Rating</p>
              <p className="text-sm font-semibold text-gray-900">{course.course_rating}</p>
            </div>
          </div>
        </div>
        
        {/* CTA 按钮 */}
        <Link
          to={`/courses/${course.id}`}
          className="block w-full text-center py-3 px-4 rounded-lg font-medium border border-green-600 text-green-600 hover:bg-green-50 transition-all duration-300"
        >
          View Details &rarr;
        </Link>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
    <div className="h-48 bg-gray-200"></div>
    <div className="p-6">
      <div className="h-6 bg-gray-200 rounded mb-2"></div>
      <div className="h-4 bg-gray-200 rounded mb-4"></div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded"></div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded mb-1"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="h-12 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);

const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<GolfCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // 动态生成城市列表（从实际数据中提取）
  const availableCities = useMemo(() => {
    // 获取所有唯一的城市
    const uniqueCities = Array.from(new Set(courses.map(c => c.city).filter(Boolean))) as string[];

    // 定义优先顺序
    const priorityOrder = ['Vancouver', 'Burnaby', 'Richmond', 'Furry Creek', 'Delta', 'Surrey'];

    // 按优先顺序排序，其他城市按字母顺序排在后面
    uniqueCities.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a);
      const indexB = priorityOrder.indexOf(b);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB; // 都在优先列表中，按优先顺序
      } else if (indexA !== -1) {
        return -1; // a在优先列表中，排前面
      } else if (indexB !== -1) {
        return 1; // b在优先列表中，排前面
      } else {
        return a.localeCompare(b); // 都不在优先列表中，按字母排序
      }
    });

    // 构建城市选项列表
    return [
      { value: 'all', label: 'All Cities' },
      ...uniqueCities.map(city => ({
        value: city,
        label: city
      }))
    ];
  }, [courses]);

  // 计算筛选后的球场和城市统计
  const { filteredCourses, cityStats } = useMemo(() => {
    let filtered = courses;

    // 按城市筛选
    if (selectedCity !== 'all') {
      filtered = filtered.filter(course => course.city === selectedCity);
    }

    // 按搜索词筛选
    if (searchTerm.trim()) {
      filtered = filtered.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
      );
    }

    // 计算每个城市的球场数量（基于搜索结果）
    const searchFiltered = searchTerm.trim()
      ? courses.filter(course => course.name.toLowerCase().includes(searchTerm.toLowerCase().trim()))
      : courses;

    const stats = availableCities.map(city => ({
      ...city,
      count: city.value === 'all'
        ? searchFiltered.length
        : searchFiltered.filter(c => c.city === city.value).length
    }));

    return { filteredCourses: filtered, cityStats: stats };
  }, [courses, selectedCity, searchTerm, availableCities]);

  // 处理搜索清除
  const handleSearchClear = () => {
    setSearchTerm('');
  };

  // 从数据库加载球场数据
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        const coursesData = await apiService.getGolfCoursesList();
        setCourses(coursesData);

        if (coursesData.length === 0) {
          setError('No golf courses found. Please check your connection and try again.');
        }
      } catch (err) {
        console.error('Error loading golf courses:', err);
        setError('Failed to load golf courses. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const handleRetry = () => {
    setError(null);
    setCourses([]);
    const loadCourses = async () => {
      try {
        setLoading(true);
        const coursesData = await apiService.getGolfCoursesList();
        setCourses(coursesData);

        if (coursesData.length === 0) {
          setError('No golf courses found. Please check your connection and try again.');
        }
      } catch (err) {
        console.error('Error loading golf courses:', err);
        setError('Failed to load golf courses. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  };

  return (
    <Layout
      title="Golf Courses | Snag Your Tee - Vancouver Golf Course Monitoring"
      description="Discover Vancouver's premier public golf courses. Browse detailed information about Langara, Fraserview, McCleery, Burnaby Mountain, and Riverway golf courses."
    >
      {/* JSON-LD 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Vancouver Golf Courses",
          "description": "Premier public golf courses in Vancouver and Burnaby",
          "itemListElement": courses.map((course, index) => ({
            "@type": "SportsActivityLocation",
            "position": index + 1,
            "name": course.name,
            "description": course.description,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": course.city,
              "addressRegion": "BC",
              "addressCountry": "CA"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "49.2827",
              "longitude": "-123.1207"
            },
            "sport": "Golf"
          }))
        })}
      </script>

      <div className="container mx-auto px-4 py-8">
        {/* 页面标题 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Greater Vancouver Golf Courses
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover premier golf courses across Vancouver, Burnaby, and Richmond. 
            Find your perfect round with detailed course information and real-time tee time availability.
          </p>
        </div>

        {/* 搜索框 */}
        <div className="mb-6">
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search golf courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 pl-12 pr-12 bg-white border border-gray-200 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={handleSearchClear}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 区域筛选 */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center items-center gap-3">
            {cityStats.map((city) => (
              <button
                key={city.value}
                onClick={() => setSelectedCity(city.value)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 flex items-center gap-2 ${
                  selectedCity === city.value
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-green-300 hover:shadow-md'
                }`}
              >
                <span>{city.label}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedCity === city.value
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {city.count}
                </span>
              </button>
            ))}
          </div>
          
          {/* 当前筛选结果提示 */}
          <div className="text-center mt-4">
            <p className="text-gray-600">
              {searchTerm ? (
                <>
                  Found <span className="font-semibold text-green-600">{filteredCourses.length}</span> courses matching{' '}
                  <span className="font-semibold text-blue-600">"{searchTerm}"</span>
                  {selectedCity !== 'all' && (
                    <> in <span className="font-semibold text-blue-600">{selectedCity}</span></>
                  )}
                </>
              ) : selectedCity === 'all' ? (
                <>
                  Showing <span className="font-semibold text-green-600">all {filteredCourses.length}</span> golf courses
                </>
              ) : (
                <>
                  Showing <span className="font-semibold text-green-600">{filteredCourses.length}</span> golf courses in{' '}
                  <span className="font-semibold text-blue-600">{selectedCity}</span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* 错误状态 */}
        {error && (
          <div className="text-center mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* 球场网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {loading ? (
            // 加载骨架屏
            [...Array(6)].map((_, index) => (
              <LoadingSkeleton key={index} />
            ))
          ) : filteredCourses.length > 0 ? (
            // 球场卡片
            filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))
          ) : (
            // 空状态
            !error && (
              <div className="col-span-full text-center py-12">
                <Circle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedCity === 'all' ? 'No Golf Courses Found' : `No Golf Courses Found in ${selectedCity}`}
                </h3>
                <p className="text-gray-600">
                  {selectedCity === 'all' 
                    ? "We couldn't find any golf courses at the moment. Please try again later."
                    : `There are no golf courses available in ${selectedCity}. Try selecting a different region.`
                  }
                </p>
                {selectedCity !== 'all' && (
                  <button
                    onClick={() => setSelectedCity('all')}
                    className="mt-4 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                  >
                    View All Courses
                  </button>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CoursesPage;